
import {
  Vue, Component, Prop, PropSync,
} from 'vue-property-decorator';
import { findBestTriggerElement } from '@/features/common/helpers/datePickerPositioning';

@Component
export default class DatePickerPortal extends Vue {
  @PropSync('dateValue', { required: true }) syncedDateValue!: string | string[];

  @Prop({ default: false }) range!: boolean;

  @Prop({ default: true }) isFuture!: boolean;

  @Prop({ type: String, required: false, default: '' }) triggerInstanceId!: string;

  @Prop({ default() { return () => true; } }) allowedDates!:
  (date: string, currentValue?: string | string[]) => boolean;

  private triggerElement: HTMLElement | null = null;

  private positionAttempts = 0;

  private maxPositionAttempts = 10;

  created() {
    this.triggerElement = this.getTriggerElement();
  }

  mounted() {
    this.$nextTick(() => {
      this.$nextTick(() => {
        this.tryAdjustPosition();
        window.addEventListener('scroll', this.adjustPosition, true);
        window.addEventListener('resize', this.adjustPosition);
      });
    });
  }

  beforeDestroy() {
    window.removeEventListener('scroll', this.adjustPosition, true);
    window.removeEventListener('resize', this.adjustPosition);
  }

  private getTriggerElement(): HTMLElement | null {
    // Strategy 1: Find by unique instance ID (most reliable for multiple instances)
    if (this.triggerInstanceId) {
      const triggerById = document.querySelector(`[data-date-picker-trigger="${this.triggerInstanceId}"]`) as HTMLElement;
      if (triggerById) {
        return findBestTriggerElement(triggerById);
      }
    }

    // Strategy 2: Find via parent component chain (before portal moves component)
    let parent = this.$parent;
    while (parent) {
      if (parent.$options.name === 'CustomPeriodPicker') {
        const parentEl = parent.$el as HTMLElement;
        return findBestTriggerElement(parentEl);
      }
      parent = parent.$parent;
    }

    // Strategy 3: Find by data attribute without ID (generic fallback)
    const triggerByAttr = document.querySelector('[data-date-picker-trigger]') as HTMLElement;
    if (triggerByAttr) {
      return findBestTriggerElement(triggerByAttr);
    }

    // Strategy 4: Find by class selector (last resort)
    const periodPickers = document.querySelectorAll('.custom-period-picker');
    if (periodPickers.length > 0) {
      const lastPicker = periodPickers[periodPickers.length - 1] as HTMLElement;
      return findBestTriggerElement(lastPicker);
    }

    // Ultimate fallback
    return this.$parent?.$el as HTMLElement;
  }

  private tryAdjustPosition(): void {
    const element = this.$refs.datePicker as HTMLElement;

    if (!element && this.positionAttempts < this.maxPositionAttempts) {
      this.positionAttempts += 1;
      setTimeout(() => this.tryAdjustPosition(), 50);
      return;
    }

    if (!this.triggerElement && this.positionAttempts < this.maxPositionAttempts) {
      this.triggerElement = this.getTriggerElement();
      this.positionAttempts += 1;
      setTimeout(() => this.tryAdjustPosition(), 50);
      return;
    }

    this.positionAttempts = 0;
    this.adjustPosition();
  }

  private adjustPosition(): void {
    const element = this.$refs.datePicker as HTMLElement;
    if (!element) {
      return;
    }

    if (!this.triggerElement || !document.body.contains(this.triggerElement)) {
      this.triggerElement = this.getTriggerElement();
    }

    const { triggerElement } = this;
    if (!triggerElement) {
      return;
    }

    const triggerRect = triggerElement.getBoundingClientRect();
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const pickerHeight = element.offsetHeight || 500;
    const pickerWidth = element.offsetWidth || 350;

    // Calculate initial position: below trigger, right-aligned
    let top = Math.round(triggerRect.bottom) + 5;
    let left = Math.round(triggerRect.right) - pickerWidth;

    // Handle left edge overflow - align to left edge of trigger if needed
    if (left < 10) {
      left = Math.max(10, Math.round(triggerRect.left));
    }

    // Handle right edge overflow - ensure picker stays within viewport
    if (left + pickerWidth > viewportWidth - 10) {
      left = Math.max(10, viewportWidth - pickerWidth - 10);
    }

    // Handle bottom overflow - position above if more space available
    if (top + pickerHeight > viewportHeight) {
      const spaceAbove = Math.round(triggerRect.top);
      const spaceBelow = viewportHeight - Math.round(triggerRect.bottom);

      if (spaceAbove > spaceBelow && spaceAbove > 300) {
        top = Math.round(triggerRect.top) - pickerHeight - 5;
      } else {
        top = Math.max(10, viewportHeight - pickerHeight - 10);
      }
    }

    // Apply positioning styles with high specificity to override any conflicts
    element.style.setProperty('position', 'fixed', 'important');
    element.style.setProperty('top', `${top}px`, 'important');
    element.style.setProperty('left', `${left}px`, 'important');
    element.style.setProperty('right', 'auto', 'important');
    element.style.setProperty('bottom', 'auto', 'important');
    element.style.setProperty('z-index', '100000', 'important');
  }

  private applyFilter() {
    this.$emit('applyFilter');
    this.closePicker();
  }

  private closePicker() {
    this.$emit('closePicker');
  }
}
